import { animated, useSpring } from '@react-spring/web';
import { useGesture } from '@use-gesture/react';
import { FiX } from 'react-icons/fi';
import {
  TransformComponent,
  TransformWrapper,
  useControls,
} from 'react-zoom-pan-pinch';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import {
  EVENTS,
  PROPERTIES,
  trackEvent,
  TRACKING_SOURCES,
} from '@/services/posthog';
import { isVideoUrl } from '@/utils/mediaUtils';
import { VideoSlideContent } from '../VideoSlideContent';
import {
  BG_OPACITY_DIVISOR,
  DISMISS_SCALE,
  DOUBLE_CLICK_STEP,
  DRAG_SCALE_DIVISOR,
  HORIZONTAL_SWIPE_DISTANCE,
  HORIZONTAL_SWIPE_MAX_THRESHOLD,
  HORIZONTAL_SWIPE_MIN_THRESHOLD,
  HORIZONTAL_SWIPE_RATIO,
  HORIZONTAL_SWIPE_VELOCITY,
  MAX_BG_OPACITY_REDUCTION,
  MAX_DRAG_SCALE_REDUCTION,
  MAX_ZOOM_SCALE,
  SPRING_FRICTION,
  SPRING_TENSION_FAST,
  SPRING_TENSION_SLOW,
  VERTICAL_SWIPE_DISMISS_DISTANCE,
  VERTICAL_SWIPE_DISMISS_THRESHOLD,
  VERTICAL_SWIPE_DISMISS_VELOCITY,
} from './ZoomableCarousel.constants';
import styles from './ZoomableCarousel.module.css';
import type { ZoomableCarouselProps } from './ZoomableCarousel.types';

const ZoomStateWatcher = ({
  onZoomChange,
  currentVideoPlaying,
}: {
  onZoomChange: (isZoomed: boolean) => void;
  currentVideoPlaying: boolean;
}): null => {
  const { state, resetTransform } = useControls();
  const isZoomed = state.scale > 1;

  useEffect(() => {
    onZoomChange(isZoomed);
  }, [isZoomed, onZoomChange]);

  useEffect(() => {
    if (currentVideoPlaying) {
      resetTransform();
    }
  }, [currentVideoPlaying, resetTransform]);

  return null;
};

export const ZoomableCarousel = ({
  images,
  initialIndex,
  onClose,
  reviewOverlays,
  productId,
}: ZoomableCarouselProps): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [currentVideoPlaying, setCurrentVideoPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const [{ trackX }, trackApi] = useSpring(() => ({
    trackX: -initialIndex * window.innerWidth,
    config: { tension: SPRING_TENSION_FAST, friction: SPRING_FRICTION },
  }));

  const [{ bgOpacity }, bgApi] = useSpring(() => ({
    bgOpacity: 1,
    config: { tension: SPRING_TENSION_FAST, friction: SPRING_FRICTION },
  }));

  const [{ y, scale: dismissScale }, dismissApi] = useSpring(() => ({
    y: 0,
    scale: 1,
    config: { tension: SPRING_TENSION_SLOW, friction: SPRING_FRICTION },
  }));

  useEffect(() => {
    trackEvent(EVENTS.PDP_V2_ZOOMABLE_CAROUSEL_OPEN, {
      [PROPERTIES.PRODUCT_ID]: productId || 0,
      [PROPERTIES.IMAGE_INDEX]: initialIndex,
      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
    });
  }, [productId, initialIndex]);

  const handleClose = useCallback((): void => {
    trackEvent(EVENTS.PDP_V2_ZOOMABLE_CAROUSEL_CLOSE, {
      [PROPERTIES.PRODUCT_ID]: productId || 0,
      [PROPERTIES.IMAGE_INDEX]: currentIndex,
      [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
    });
    onClose();
  }, [onClose, productId, currentIndex]);

  useEffect(() => {
    const handleResize = (): void => {
      trackApi.start({
        trackX: -currentIndex * window.innerWidth,
        immediate: true,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex, trackApi]);

  useEffect(() => {
    trackApi.start({ trackX: -currentIndex * window.innerWidth });
  }, [currentIndex, trackApi]);

  useEffect(() => {
    setCurrentVideoPlaying(false);
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      trackEvent(EVENTS.PDP_V2_ZOOMABLE_CAROUSEL_SWIPE, {
        [PROPERTIES.PRODUCT_ID]: productId || 0,
        [PROPERTIES.FROM_INDEX]: currentIndex,
        [PROPERTIES.TO_INDEX]: currentIndex + 1,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
      setCurrentIndex((prev) => prev + 1);
    }
  }, [currentIndex, images.length, productId]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      trackEvent(EVENTS.PDP_V2_ZOOMABLE_CAROUSEL_SWIPE, {
        [PROPERTIES.PRODUCT_ID]: productId || 0,
        [PROPERTIES.FROM_INDEX]: currentIndex,
        [PROPERTIES.TO_INDEX]: currentIndex - 1,
        [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
      });
      setCurrentIndex((prev) => prev - 1);
    }
  }, [currentIndex, productId]);

  const handleZoomChange = useCallback(
    (zoomed: boolean) => {
      if (zoomed !== isImageZoomed) {
        trackEvent(EVENTS.PDP_V2_ZOOMABLE_CAROUSEL_ZOOM, {
          [PROPERTIES.PRODUCT_ID]: productId || 0,
          [PROPERTIES.IMAGE_INDEX]: currentIndex,
          [PROPERTIES.IS_ZOOMED]: zoomed,
          [PROPERTIES.SOURCE]: TRACKING_SOURCES.PDP_V2,
        });
        setIsImageZoomed(zoomed);
      }
    },
    [isImageZoomed, currentIndex, productId]
  );

  const bind = useGesture(
    {
      onDrag: ({
        active,
        movement: [mx, my],
        velocity: [vx, vy],
        memo,
        event,
      }) => {
        if (isImageZoomed) return;

        if (event.cancelable) event.preventDefault();

        let dragType = memo?.dragType;
        if (!dragType) {
          dragType = Math.abs(my) > Math.abs(mx) ? 'vertical' : 'horizontal';
        }

        if (dragType === 'vertical') {
          if (!active) {
            if (
              Math.abs(my) > VERTICAL_SWIPE_DISMISS_THRESHOLD ||
              (Math.abs(my) > VERTICAL_SWIPE_DISMISS_DISTANCE &&
                Math.abs(vy) > VERTICAL_SWIPE_DISMISS_VELOCITY)
            ) {
              bgApi.start({ bgOpacity: 0 });
              dismissApi.start({
                y: my > 0 ? window.innerHeight : -window.innerHeight,
                scale: DISMISS_SCALE,
                onRest: handleClose,
              });
            } else {
              bgApi.start({ bgOpacity: 1 });
              dismissApi.start({ y: 0, scale: 1 });
            }
          } else {
            const dragScale =
              1 -
              Math.min(
                MAX_DRAG_SCALE_REDUCTION,
                Math.abs(my) / DRAG_SCALE_DIVISOR
              );
            dismissApi.start({ y: my, scale: dragScale, immediate: true });
            bgApi.start({
              bgOpacity:
                1 -
                Math.min(
                  MAX_BG_OPACITY_REDUCTION,
                  Math.abs(my) / BG_OPACITY_DIVISOR
                ),
              immediate: true,
            });
          }
        } else {
          const width = window.innerWidth;
          const swipeThreshold = Math.min(
            HORIZONTAL_SWIPE_MAX_THRESHOLD,
            Math.max(
              HORIZONTAL_SWIPE_MIN_THRESHOLD,
              width * HORIZONTAL_SWIPE_RATIO
            )
          );

          if (!active) {
            const isSwipeLeft =
              mx < -swipeThreshold ||
              (mx < -HORIZONTAL_SWIPE_DISTANCE &&
                vx > HORIZONTAL_SWIPE_VELOCITY);
            const isSwipeRight =
              mx > swipeThreshold ||
              (mx > HORIZONTAL_SWIPE_DISTANCE &&
                vx > HORIZONTAL_SWIPE_VELOCITY);

            if (isSwipeLeft && currentIndex < images.length - 1) {
              handleNext();
            } else if (isSwipeRight && currentIndex > 0) {
              handlePrev();
            } else {
              trackApi.start({ trackX: -currentIndex * width });
            }
          } else {
            trackApi.start({
              trackX: -currentIndex * width + mx,
              immediate: true,
            });
          }
        }

        return { dragType };
      },
    },
    {
      drag: {
        filterTaps: true,
        enabled: !isImageZoomed,
      },
    }
  );

  return (
    <animated.div
      ref={containerRef}
      className={styles.fullScreenOverlay}
      style={{
        backgroundColor: bgOpacity.to((o) => `rgba(0, 0, 0, ${o * 0.95})`),
      }}
    >
      <div className={styles.fullScreenHeader}>
        <button
          className={styles.fullScreenCloseBtn}
          aria-label="Close Fullscreen"
          onClick={handleClose}
        >
          <FiX size={24} />
        </button>
        <div className={styles.fullScreenProgress} />
      </div>

      <animated.div
        className={styles.fullScreenViewport}
        {...bind()}
        style={{
          touchAction: isImageZoomed ? 'none' : 'pan-y',
          y,
          scale: dismissScale,
        }}
      >
        <animated.div
          className={styles.fullScreenTrack}
          style={{
            transform: trackX.to((val) => `translateX(${val}px)`),
            width: `${images.length * 100}%`,
          }}
        >
          {images.map((imgUrl, index) => {
            const isCurrent = index === currentIndex;
            const isVideo = isVideoUrl(imgUrl);
            return (
              <div key={index} className={styles.fullScreenSlide}>
                {isCurrent ? (
                  <TransformWrapper
                    disabled={isVideo && currentVideoPlaying}
                    initialScale={1}
                    centerOnInit={true}
                    minScale={1}
                    maxScale={MAX_ZOOM_SCALE}
                    doubleClick={{ disabled: isVideo, step: DOUBLE_CLICK_STEP }}
                    wheel={{ disabled: false }}
                    panning={{ disabled: false }}
                  >
                    <ZoomStateWatcher
                      currentVideoPlaying={currentVideoPlaying}
                      onZoomChange={handleZoomChange}
                    />
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                      }}
                      contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {isVideo ? (
                        <VideoSlideContent
                          src={imgUrl}
                          isCurrent={isCurrent}
                          onPlayStateChange={setCurrentVideoPlaying}
                        />
                      ) : (
                        <img
                          src={imgUrl}
                          alt={`Product view ${index + 1}`}
                          className={styles.fullScreenImage}
                          draggable={false}
                        />
                      )}
                    </TransformComponent>
                  </TransformWrapper>
                ) : isVideo ? (
                  <VideoSlideContent src={imgUrl} isCurrent={isCurrent} />
                ) : (
                  <img
                    src={imgUrl}
                    alt={`Product view ${index + 1}`}
                    className={styles.fullScreenImage}
                    draggable={false}
                  />
                )}
              </div>
            );
          })}
        </animated.div>
      </animated.div>

      {reviewOverlays && reviewOverlays[currentIndex] && (
        <div className={styles.reviewOverlayContainer}>
          <div className={styles.reviewUserRow}>
            <span className={styles.reviewUserName}>
              {reviewOverlays[currentIndex].username}
            </span>
            <span className={styles.reviewStars}>
              {reviewOverlays[currentIndex].rating} ★
            </span>
          </div>
          <p className={styles.reviewText}>
            {reviewOverlays[currentIndex].text}
          </p>
        </div>
      )}
    </animated.div>
  );
};

export default ZoomableCarousel;
